import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api, setAdminToken, loadAdminToken, formatMXN } from "../lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from "@/components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Tabs,
  TabsList,
  TabsTrigger,
  TabsContent,
} from "@/components/ui/tabs";
import { Switch } from "@/components/ui/switch";
import { Pencil, Trash2, LogOut, Save, ArrowLeft, Plus, Image as ImageIcon, Map as MapIcon, FileText, Sparkles, Camera } from "lucide-react";
import ImagePicker from "../components/ImagePicker";
import SitePlanEditor from "../components/SitePlanEditor";
import ContentEditor from "../components/ContentEditor";
import AmenityEditor from "../components/AmenityEditor";
import MediaEditor from "../components/MediaEditor";
import { resolveImage } from "../lib/api";

function Login({ onLogin }) {
  const [code, setCode] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await api.post("/admin/login", { code: code.trim() });
      setAdminToken(res.data.token);
      toast.success("¡Bienvenido!");
      onLogin();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      const msg = typeof detail === "string"
        ? detail
        : "Código incorrecto. Genera uno nuevo en tu app de autenticación.";
      toast.error(msg);
      setCode("");
    } finally {
      setLoading(false);
    }
  };

  // Auto-submit when 6 digits entered
  useEffect(() => {
    if (code.length === 6 && /^\d{6}$/.test(code) && !loading) {
      submit(new Event("auto"));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [code]);

  const handleCodeChange = (e) => {
    const v = e.target.value.replace(/\D/g, "").slice(0, 6);
    setCode(v);
  };

  return (
    <div className="min-h-screen bg-[var(--c-bg-deep)] flex items-center justify-center px-6 relative overflow-hidden">
      <div className="orb w-[480px] h-[480px] bg-[var(--c-bio-green)]/30 -top-32 -left-32" />
      <div className="orb w-[400px] h-[400px] bg-[var(--c-firefly-gold)]/25 bottom-0 -right-32" />
      <form
        onSubmit={submit}
        data-testid="admin-login-form"
        className="relative w-full max-w-md rounded-3xl bg-[var(--c-bg-forest)]/80 backdrop-blur-xl border border-[var(--c-bio-green)]/25 p-8 shadow-[0_50px_120px_-30px_rgba(0,0,0,0.7)]"
      >
        <div className="flex items-center gap-2 mb-6">
          <span className="w-10 h-10 rounded-full bg-gradient-to-br from-[var(--c-bio-green)] via-[var(--c-firefly-gold)] to-[var(--c-moss)] flex items-center justify-center text-[#0B1A0F] font-serif text-lg shadow-[0_0_25px_rgba(184,224,122,0.5)]">
            P
          </span>
          <div className="leading-tight">
            <div className="font-serif text-2xl text-[var(--c-text)]">Pextlán</div>
            <div className="font-fairy text-base text-[var(--c-firefly-gold)] -mt-1">panel admin</div>
          </div>
        </div>
        <h1 className="font-serif text-3xl text-[var(--c-text)]">Verificación en dos pasos</h1>
        <p className="mt-2 text-sm text-[var(--c-text)]/65">
          Abre <span className="text-[var(--c-firefly-gold)] font-medium">Microsoft Authenticator</span> e ingresa el código de 6 dígitos.
        </p>
        <label className="mt-6 block text-xs uppercase tracking-widest text-[var(--c-bio-green)]">
          Código de autenticación
        </label>
        <Input
          data-testid="admin-token-input"
          type="text"
          inputMode="numeric"
          pattern="[0-9]*"
          autoComplete="one-time-code"
          autoFocus
          value={code}
          onChange={handleCodeChange}
          placeholder="000000"
          maxLength={6}
          className="mt-2 bg-[var(--c-bg-deep)] border-[var(--c-bio-green)]/30 text-[var(--c-text)] placeholder:text-[var(--c-text)]/25 rounded-xl font-mono text-3xl text-center tracking-[0.6em] h-16"
          required
        />
        <div className="mt-2 text-[11px] text-[var(--c-text)]/45 text-center font-mono">
          el código cambia cada 30 segundos
        </div>
        <Button
          data-testid="admin-login-submit"
          type="submit"
          disabled={loading || code.length !== 6}
          className="mt-6 w-full rounded-full bg-gradient-to-r from-[var(--c-firefly-gold)] to-[var(--c-bio-green)] hover:opacity-95 text-[#0B1A0F] h-12 font-bold btn-lift disabled:opacity-50"
        >
          {loading ? "Verificando..." : "Entrar al cuento"}
        </Button>
        <a href="/" className="mt-4 inline-flex items-center gap-1 text-xs text-[var(--c-text)]/60 hover:text-[var(--c-firefly-gold)]">
          <ArrowLeft className="w-3 h-3" /> Volver al sitio
        </a>
      </form>
    </div>
  );
}

function EditDialog({ open, onOpenChange, property, onSave }) {
  const [form, setForm] = useState(property || {});
  const [pickerMode, setPickerMode] = useState(null); // 'main' | 'gallery' | null
  useEffect(() => { setForm(property || {}); }, [property]);

  if (!property) return null;
  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e?.target ? e.target.value : e }));
  const galleryArr = Array.isArray(form.gallery) ? form.gallery : (form.gallery ? String(form.gallery).split("\n").filter(Boolean) : []);

  const save = async () => {
    const payload = {
      name: form.name,
      location: form.location,
      price: Number(form.price),
      surface: Number(form.surface),
      image: form.image,
      gallery: galleryArr,
      description: form.description,
      features: typeof form.features === "string" ? form.features.split("\n").filter(Boolean) : form.features,
      map_embed_url: form.map_embed_url,
      available: form.available,
    };
    await onSave(property.id, payload);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto bg-[var(--c-bg-deep)] border-[var(--c-bio-green)]/25 text-[var(--c-text)]">
        <DialogHeader>
          <DialogTitle className="font-serif text-2xl text-[var(--c-text)]">Editar terreno</DialogTitle>
          <DialogDescription className="text-[var(--c-text)]/65">Modifica los detalles del terreno y guarda los cambios.</DialogDescription>
        </DialogHeader>
        <div className="grid sm:grid-cols-2 gap-3">
          <div>
            <label className="text-xs uppercase tracking-widest text-[var(--c-bio-green)]">Nombre</label>
            <Input value={form.name || ""} onChange={update("name")} className="bg-[var(--c-bg-forest)] border-[var(--c-bio-green)]/25 text-[var(--c-text)] rounded-xl" />
          </div>
          <div>
            <label className="text-xs uppercase tracking-widest text-[var(--c-bio-green)]">Ubicación</label>
            <Input value={form.location || ""} onChange={update("location")} className="bg-[var(--c-bg-forest)] border-[var(--c-bio-green)]/25 text-[var(--c-text)] rounded-xl" />
          </div>
          <div>
            <label className="text-xs uppercase tracking-widest text-[var(--c-bio-green)]">Precio (MXN)</label>
            <Input type="number" value={form.price || 0} onChange={update("price")} className="bg-[var(--c-bg-forest)] border-[var(--c-bio-green)]/25 text-[var(--c-text)] rounded-xl" />
          </div>
          <div>
            <label className="text-xs uppercase tracking-widest text-[var(--c-bio-green)]">Superficie (m²)</label>
            <Input type="number" value={form.surface || 0} onChange={update("surface")} className="bg-[var(--c-bg-forest)] border-[var(--c-bio-green)]/25 text-[var(--c-text)] rounded-xl" />
          </div>
        </div>

        {/* Main image picker */}
        <div className="mt-4">
          <label className="text-xs uppercase tracking-widest text-[var(--c-bio-green)]">Imagen principal</label>
          <div className="mt-2 flex items-center gap-3">
            <div className="w-28 h-20 rounded-lg overflow-hidden border border-[var(--c-bio-green)]/20 bg-[var(--c-bg-forest)] flex items-center justify-center">
              {form.image ? (
                <img src={resolveImage(form.image)} alt="Principal" className="w-full h-full object-cover" />
              ) : (
                <ImageIcon className="w-6 h-6 text-[var(--c-text)]/45" />
              )}
            </div>
            <div className="flex-1 flex flex-col gap-2">
              <Input
                value={form.image || ""}
                onChange={update("image")}
                placeholder="URL externa o usa la biblioteca"
                className="bg-[var(--c-bg-forest)] border-[var(--c-bio-green)]/25 text-[var(--c-text)] rounded-xl"
              />
              <Button type="button" variant="outline" size="sm" onClick={() => setPickerMode("main")} data-testid="pick-main-image"
                className="bg-transparent border-[var(--c-bio-green)]/30 text-[var(--c-bio-green)] hover:bg-[var(--c-bio-green)] hover:text-[#0B1A0F]">
                <ImageIcon className="w-4 h-4 mr-1" /> Elegir de biblioteca
              </Button>
            </div>
          </div>
        </div>

        {/* Gallery picker */}
        <div className="mt-4">
          <div className="flex items-center justify-between">
            <label className="text-xs uppercase tracking-widest text-[var(--c-bio-green)]">Galería</label>
            <Button type="button" variant="outline" size="sm" onClick={() => setPickerMode("gallery")} data-testid="pick-gallery"
              className="bg-transparent border-[var(--c-bio-green)]/30 text-[var(--c-bio-green)] hover:bg-[var(--c-bio-green)] hover:text-[#0B1A0F]">
              <ImageIcon className="w-4 h-4 mr-1" /> Agregar / editar
            </Button>
          </div>
          {galleryArr.length === 0 ? (
            <div className="mt-2 text-xs text-[var(--c-text)]/55 border border-dashed border-[var(--c-bio-green)]/20 rounded-lg p-4 text-center">
              Sin imágenes en la galería. Agrega desde la biblioteca.
            </div>
          ) : (
            <div className="mt-2 grid grid-cols-4 sm:grid-cols-6 gap-2">
              {galleryArr.map((url) => (
                <div key={url} className="relative group rounded-lg overflow-hidden border border-[var(--c-bio-green)]/20">
                  <img src={resolveImage(url)} alt="" className="w-full aspect-square object-cover" />
                  <button
                    type="button"
                    onClick={() => setForm((f) => ({ ...f, gallery: galleryArr.filter((u) => u !== url) }))}
                    className="absolute top-1 right-1 w-6 h-6 rounded-full bg-black/70 text-white opacity-0 group-hover:opacity-100 flex items-center justify-center"
                    aria-label="Quitar"
                  >
                    <Trash2 className="w-3 h-3" />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="mt-4">
          <label className="text-xs uppercase tracking-widest text-[var(--c-bio-green)]">Descripción</label>
          <Textarea rows={3} value={form.description || ""} onChange={update("description")} className="bg-[var(--c-bg-forest)] border-[var(--c-bio-green)]/25 text-[var(--c-text)] rounded-xl" />
        </div>
        <div className="mt-3">
          <label className="text-xs uppercase tracking-widest text-[var(--c-bio-green)]">Características (una por línea)</label>
          <Textarea
            rows={3}
            value={Array.isArray(form.features) ? form.features.join("\n") : (form.features || "")}
            onChange={update("features")}
            className="bg-[var(--c-bg-forest)] border-[var(--c-bio-green)]/25 text-[var(--c-text)] rounded-xl"
          />
        </div>
        <div className="mt-3">
          <label className="text-xs uppercase tracking-widest text-[var(--c-bio-green)]">URL del mapa (Google Maps embed)</label>
          <Input value={form.map_embed_url || ""} onChange={update("map_embed_url")} className="bg-[var(--c-bg-forest)] border-[var(--c-bio-green)]/25 text-[var(--c-text)] rounded-xl" />
        </div>
        <div className="mt-3 flex items-center gap-2">
          <Switch checked={!!form.available} onCheckedChange={(v) => setForm((f) => ({ ...f, available: v }))} />
          <span className="text-sm text-[var(--c-text)]">Disponible</span>
        </div>
        <DialogFooter className="mt-4">
          <Button variant="outline" onClick={() => onOpenChange(false)}
            className="bg-transparent border-[var(--c-bio-green)]/30 text-[var(--c-text)] hover:bg-[var(--c-bg-forest)]">
            Cancelar
          </Button>
          <Button onClick={save} className="bg-gradient-to-r from-[var(--c-firefly-gold)] to-[var(--c-bio-green)] hover:opacity-90 text-[#0B1A0F] font-semibold" data-testid="save-property">
            <Save className="w-4 h-4 mr-1" /> Guardar
          </Button>
        </DialogFooter>

        <ImagePicker
          open={pickerMode === "main"}
          onOpenChange={(v) => !v && setPickerMode(null)}
          multiple={false}
          initialSelected={form.image ? [form.image] : []}
          onConfirm={(url) => setForm((f) => ({ ...f, image: url }))}
        />
        <ImagePicker
          open={pickerMode === "gallery"}
          onOpenChange={(v) => !v && setPickerMode(null)}
          multiple={true}
          initialSelected={galleryArr}
          onConfirm={(arr) => setForm((f) => ({ ...f, gallery: arr }))}
        />
      </DialogContent>
    </Dialog>
  );
}

export default function Admin() {
  const [authed, setAuthed] = useState(!!loadAdminToken());
  const [properties, setProperties] = useState([]);
  const [leads, setLeads] = useState([]);
  const [interestRate, setInterestRate] = useState(12);
  const [whatsapp, setWhatsapp] = useState("");
  const [editOpen, setEditOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const navigate = useNavigate();

  const loadAll = async () => {
    try {
      const [p, s, l] = await Promise.all([
        api.get("/properties"),
        api.get("/settings"),
        api.get("/leads"),
      ]);
      setProperties(p.data);
      setInterestRate(s.data.interest_rate);
      setWhatsapp(s.data.whatsapp || "");
      setLeads(l.data);
    } catch (e) {
      if (e?.response?.status === 401) {
        setAdminToken(null);
        setAuthed(false);
      }
    }
  };

  useEffect(() => {
    if (authed) loadAll();
  }, [authed]);

  if (!authed) return <Login onLogin={() => setAuthed(true)} />;

  const saveProperty = async (id, payload) => {
    try {
      await api.put(`/properties/${id}`, payload);
      toast.success("Terreno actualizado");
      setEditOpen(false);
      loadAll();
    } catch {
      toast.error("No se pudo guardar");
    }
  };

  const deleteProperty = async (id) => {
    if (!window.confirm("¿Eliminar este terreno?")) return;
    try {
      await api.delete(`/properties/${id}`);
      toast.success("Terreno eliminado");
      loadAll();
    } catch {
      toast.error("No se pudo eliminar");
    }
  };

  const createProperty = async () => {
    try {
      const res = await api.post("/properties", {
        name: "Nuevo terreno",
        location: "Coatepec",
        price: 500000,
        surface: 400,
        image: "https://images.unsplash.com/photo-1744986924535-ab6ef975f890?crop=entropy&cs=srgb&fm=jpg&q=85",
        gallery: [],
        description: "",
        features: [],
        map_embed_url: "",
        available: true,
      });
      toast.success("Terreno creado");
      setEditing(res.data);
      setEditOpen(true);
      loadAll();
    } catch {
      toast.error("No se pudo crear");
    }
  };

  const saveSettings = async () => {
    try {
      await api.put("/settings", { interest_rate: Number(interestRate), whatsapp });
      toast.success("Configuración guardada");
    } catch {
      toast.error("No se pudo guardar configuración");
    }
  };

  const logout = () => {
    setAdminToken(null);
    setAuthed(false);
    navigate("/");
  };

  return (
    <div className="min-h-screen bg-[var(--c-bg-deep)] text-[var(--c-text)] relative">
      <header className="border-b border-[var(--c-bio-green)]/15 bg-[var(--c-bg-deep)]/85 backdrop-blur-xl sticky top-0 z-30">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 py-4 flex items-center justify-between">
          <div className="flex items-center gap-2.5">
            <span className="w-10 h-10 rounded-full bg-gradient-to-br from-[var(--c-bio-green)] via-[var(--c-firefly-gold)] to-[var(--c-moss)] flex items-center justify-center text-[#0B1A0F] font-serif text-lg shadow-[0_0_20px_rgba(184,224,122,0.5)]">P</span>
            <div className="leading-tight">
              <span className="block font-serif text-xl text-[var(--c-text)]">Pextlán</span>
              <span className="block font-fairy text-sm text-[var(--c-firefly-gold)] -mt-1">panel admin</span>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <a href="/" className="text-xs text-[var(--c-text)]/70 hover:text-[var(--c-firefly-gold)] inline-flex items-center gap-1">
              <ArrowLeft className="w-3 h-3" /> Ver sitio
            </a>
            <Button
              variant="outline"
              size="sm"
              onClick={logout}
              data-testid="admin-logout"
              className="bg-transparent border-[var(--c-bio-green)]/30 text-[var(--c-text)] hover:bg-[var(--c-firefly-gold)] hover:text-[#0B1A0F] hover:border-[var(--c-firefly-gold)]"
            >
              <LogOut className="w-4 h-4 mr-1" /> Salir
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 sm:px-8 py-10 relative">
        <Tabs defaultValue="properties" className="w-full">
          <TabsList className="bg-[var(--c-bg-forest)] border border-[var(--c-bio-green)]/20 text-[var(--c-text)]/80 p-1 flex-wrap h-auto">
            <TabsTrigger value="properties" data-testid="tab-properties" className="data-[state=active]:bg-[var(--c-firefly-gold)] data-[state=active]:text-[#0B1A0F] text-[var(--c-text)]/80">Terrenos</TabsTrigger>
            <TabsTrigger value="map" data-testid="tab-map" className="data-[state=active]:bg-[var(--c-firefly-gold)] data-[state=active]:text-[#0B1A0F] text-[var(--c-text)]/80"><MapIcon className="w-3.5 h-3.5 mr-1" />Mapa</TabsTrigger>
            <TabsTrigger value="content" data-testid="tab-content" className="data-[state=active]:bg-[var(--c-firefly-gold)] data-[state=active]:text-[#0B1A0F] text-[var(--c-text)]/80"><FileText className="w-3.5 h-3.5 mr-1" />Contenido</TabsTrigger>
            <TabsTrigger value="amenities" data-testid="tab-amenities" className="data-[state=active]:bg-[var(--c-firefly-gold)] data-[state=active]:text-[#0B1A0F] text-[var(--c-text)]/80"><Sparkles className="w-3.5 h-3.5 mr-1" />Amenidades</TabsTrigger>
            <TabsTrigger value="media" data-testid="tab-media" className="data-[state=active]:bg-[var(--c-firefly-gold)] data-[state=active]:text-[#0B1A0F] text-[var(--c-text)]/80"><Camera className="w-3.5 h-3.5 mr-1" />Galería</TabsTrigger>
            <TabsTrigger value="leads" data-testid="tab-leads" className="data-[state=active]:bg-[var(--c-firefly-gold)] data-[state=active]:text-[#0B1A0F] text-[var(--c-text)]/80">Leads ({leads.length})</TabsTrigger>
            <TabsTrigger value="settings" data-testid="tab-settings" className="data-[state=active]:bg-[var(--c-firefly-gold)] data-[state=active]:text-[#0B1A0F] text-[var(--c-text)]/80">Configuración</TabsTrigger>
          </TabsList>

          <TabsContent value="properties" className="mt-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-serif text-3xl text-[var(--c-text)]">Terrenos</h2>
              <Button onClick={createProperty} data-testid="admin-create-property" className="bg-gradient-to-r from-[var(--c-firefly-gold)] to-[var(--c-bio-green)] hover:opacity-90 text-[#0B1A0F] rounded-full font-semibold">
                <Plus className="w-4 h-4 mr-1" /> Nuevo terreno
              </Button>
            </div>
            <div className="rounded-2xl border border-[var(--c-bio-green)]/15 bg-[var(--c-bg-forest)]/60 backdrop-blur overflow-hidden">
              <Table>
                <TableHeader>
                  <TableRow className="border-[var(--c-bio-green)]/15 hover:bg-transparent">
                    <TableHead className="text-[var(--c-firefly-gold)] uppercase tracking-wider text-[10px]">Nombre</TableHead>
                    <TableHead className="text-[var(--c-firefly-gold)] uppercase tracking-wider text-[10px]">Ubicación</TableHead>
                    <TableHead className="text-[var(--c-firefly-gold)] uppercase tracking-wider text-[10px]">Precio</TableHead>
                    <TableHead className="text-[var(--c-firefly-gold)] uppercase tracking-wider text-[10px]">Superficie</TableHead>
                    <TableHead className="text-[var(--c-firefly-gold)] uppercase tracking-wider text-[10px]">Disp.</TableHead>
                    <TableHead className="text-right text-[var(--c-firefly-gold)] uppercase tracking-wider text-[10px]">Acciones</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {properties.map((p) => (
                    <TableRow key={p.id} className="border-[var(--c-bio-green)]/10 hover:bg-[var(--c-bg-deep)]/40">
                      <TableCell className="font-medium text-[var(--c-text)]">{p.name}</TableCell>
                      <TableCell className="text-[var(--c-text)]/75">{p.location}</TableCell>
                      <TableCell className="text-[var(--c-bio-green)] font-medium">{formatMXN(p.price)}</TableCell>
                      <TableCell className="text-[var(--c-text)]/75">{p.surface} m²</TableCell>
                      <TableCell className="text-[var(--c-text)]/75">{p.available ? "Sí" : "No"}</TableCell>
                      <TableCell className="text-right space-x-2">
                        <Button
                          variant="outline"
                          size="sm"
                          data-testid={`edit-property-${p.id}`}
                          onClick={() => { setEditing(p); setEditOpen(true); }}
                          className="bg-transparent border-[var(--c-bio-green)]/30 text-[var(--c-bio-green)] hover:bg-[var(--c-bio-green)] hover:text-[#0B1A0F]"
                        >
                          <Pencil className="w-3.5 h-3.5" />
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          data-testid={`delete-property-${p.id}`}
                          onClick={() => deleteProperty(p.id)}
                          className="bg-transparent border-red-400/30 text-red-400 hover:bg-red-500 hover:text-white hover:border-red-500"
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </TabsContent>

          <TabsContent value="map" className="mt-6">
            <SitePlanEditor properties={properties} onSaved={loadAll} />
          </TabsContent>

          <TabsContent value="content" className="mt-6">
            <ContentEditor />
          </TabsContent>

          <TabsContent value="amenities" className="mt-6">
            <AmenityEditor />
          </TabsContent>

          <TabsContent value="media" className="mt-6">
            <MediaEditor />
          </TabsContent>

          <TabsContent value="leads" className="mt-6">
            <h2 className="font-serif text-3xl text-[var(--c-text)] mb-4">Prospectos</h2>
            <div className="rounded-2xl border border-[var(--c-bio-green)]/15 bg-[var(--c-bg-forest)]/60 backdrop-blur overflow-hidden">
              <Table>
                <TableHeader>
                  <TableRow className="border-[var(--c-bio-green)]/15 hover:bg-transparent">
                    <TableHead className="text-[var(--c-firefly-gold)] uppercase tracking-wider text-[10px]">Nombre</TableHead>
                    <TableHead className="text-[var(--c-firefly-gold)] uppercase tracking-wider text-[10px]">Teléfono</TableHead>
                    <TableHead className="text-[var(--c-firefly-gold)] uppercase tracking-wider text-[10px]">Email</TableHead>
                    <TableHead className="text-[var(--c-firefly-gold)] uppercase tracking-wider text-[10px]">Terreno</TableHead>
                    <TableHead className="text-[var(--c-firefly-gold)] uppercase tracking-wider text-[10px]">Mensaje</TableHead>
                    <TableHead className="text-[var(--c-firefly-gold)] uppercase tracking-wider text-[10px]">Fecha</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {leads.length === 0 && (
                    <TableRow className="border-[var(--c-bio-green)]/10">
                      <TableCell colSpan={6} className="text-center text-[var(--c-text)]/50 py-10">
                        Aún no hay prospectos.
                      </TableCell>
                    </TableRow>
                  )}
                  {leads.map((l) => (
                    <TableRow key={l.id} className="border-[var(--c-bio-green)]/10 hover:bg-[var(--c-bg-deep)]/40">
                      <TableCell className="font-medium text-[var(--c-text)]">{l.name}</TableCell>
                      <TableCell className="text-[var(--c-bio-green)]">{l.phone}</TableCell>
                      <TableCell className="text-[var(--c-text)]/75">{l.email || "—"}</TableCell>
                      <TableCell className="text-[var(--c-text)]/75">{l.property_interest || "—"}</TableCell>
                      <TableCell className="max-w-xs truncate text-[var(--c-text)]/75">{l.message || "—"}</TableCell>
                      <TableCell className="text-[var(--c-text)]/55 text-xs">{new Date(l.created_at).toLocaleString("es-MX")}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </TabsContent>

          <TabsContent value="settings" className="mt-6">
            <h2 className="font-serif text-3xl text-[var(--c-text)] mb-4">Configuración</h2>
            <div className="max-w-xl rounded-2xl border border-[var(--c-bio-green)]/15 bg-[var(--c-bg-forest)]/60 backdrop-blur p-6">
              <label className="text-xs uppercase tracking-widest text-[var(--c-bio-green)]">
                Tasa de interés anual (%)
              </label>
              <Input
                data-testid="settings-interest-rate"
                type="number"
                step="0.1"
                value={interestRate}
                onChange={(e) => setInterestRate(e.target.value)}
                className="mt-1 bg-[var(--c-bg-deep)] border-[var(--c-bio-green)]/30 text-[var(--c-text)] rounded-xl"
              />
              <label className="mt-4 block text-xs uppercase tracking-widest text-[var(--c-bio-green)]">
                WhatsApp contacto
              </label>
              <Input
                data-testid="settings-whatsapp"
                value={whatsapp}
                onChange={(e) => setWhatsapp(e.target.value)}
                className="mt-1 bg-[var(--c-bg-deep)] border-[var(--c-bio-green)]/30 text-[var(--c-text)] rounded-xl"
              />
              <Button
                data-testid="save-settings"
                onClick={saveSettings}
                className="mt-6 bg-gradient-to-r from-[var(--c-firefly-gold)] to-[var(--c-bio-green)] hover:opacity-90 text-[#0B1A0F] rounded-full font-semibold"
              >
                <Save className="w-4 h-4 mr-1" /> Guardar cambios
              </Button>
            </div>
          </TabsContent>
        </Tabs>
      </main>

      <EditDialog
        open={editOpen}
        onOpenChange={setEditOpen}
        property={editing}
        onSave={saveProperty}
      />
    </div>
  );
}
